import {useApplicationData} from '@/Core/hooks/useApplicationData';
import {useUnderwritingAnalytics} from '@/Core/hooks/useUnderwritingAnalytics';
import {CalcfocusReportRequest} from '@amplify/athena-models';
import {Box, CircularProgress, Grid, Typography, makeStyles} from '@material-ui/core';
import React, {useEffect, useRef, useState} from 'react';
import {AgenuimContextType} from '../../../Core/agenium/ageniumContext';
import {formatCurrency} from '../../../Core/helpers/formatCurrency';
import {useAgeniumUser} from '../../../Core/hooks/useAgeniumUser';
import {ParameterStore} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {IS_PRODUCTION} from '../../../constants';
import {ContactFormStore} from '../../ContactForm/ContactFormStore/ContactFormStore';
import {Outlist} from '../InstantAppModal';
import {getApplicationRequirements, submitDocusign} from '../api/instantIul';
import {useCommonModalStyles} from './common/modalStyles';
import {getAgeniumCalcfocusRequest} from './utils';
import FixedNavigation from './common/FixedNavigation';

const useStyles = makeStyles(() => ({
    warningText: {
        color: '#d32f2f',
        fontWeight: 500,
        textAlign: 'center',
    },
}));

interface SendDocusignProps {
    parameterStore: ParameterStore;
    contactFormStore: ContactFormStore;
    onExit: () => void;
    onDocusignSignComplete: () => void;
    ageniumContext: AgenuimContextType;
    onBack?: () => void;
    onDecisionRedirect?: (decision: 'REFERRED' | 'DECLINED' | 'CANCELLED') => void;
    quoteResponse?: Outlist[];
}

export const SendDocusign: React.FC<SendDocusignProps> = ({
    parameterStore,
    contactFormStore,
    onBack,
    onExit,
    onDocusignSignComplete,
    ageniumContext,
    onDecisionRedirect,
    quoteResponse,
}) => {
    const [isAwaitingSignature, setIsAwaitingSignature] = useState(false);
    const [isCheckForDocusignCompleteDone, setIsCheckForDocusignCompleteDone] = useState(false);
    const commonClasses = useCommonModalStyles();
    const classes = useStyles();
    const {ageniumUser} = useAgeniumUser();
    const {applicationData} = useApplicationData();
    const hasLoadedAppData = useRef(false);
    const {storeDecision} = useUnderwritingAnalytics({
        applicationId: ageniumContext.applicationId,
        formId: parameterStore.formid,
    });

    function getMaxFaceAmount() {
        return Number(ageniumContext.applicationData?.find((t) => t.key === 'Case_FinancialCalc_MaxFaceAmount')?.value);
    }

    function getDeathBenefit() {
        return Number(quoteResponse?.find((qr) => qr.name === 'Initial death benefit')?.value ?? 0);
    }

    const isDeathBenefitExceedsMax =
        ageniumContext.applicationData && quoteResponse ? getDeathBenefit() > getMaxFaceAmount() : false;

    // Check application decision and redirect if not approved
    useEffect(() => {
        if (
            ageniumContext.applicationDecision &&
            ageniumContext.applicationDecision !== 'APPROVED' &&
            onDecisionRedirect
        ) {
            storeDecision({decision: ageniumContext.applicationDecision});
            onDecisionRedirect(ageniumContext.applicationDecision);
        }
    }, [ageniumContext.applicationDecision, onDecisionRedirect]);

    async function onSendDocusignClick() {
        setIsAwaitingSignature(true);
        const formId = parameterStore.formid as string;
        const instantIulApplicationId = parameterStore.instantIulApplicationId as string;

        // Use saved prosper data from approval step to prevent modification of quote values
        const savedProsperData = applicationData?.prosperData;
        const calcfocusReportRequest = getAgeniumCalcfocusRequest(
            ageniumContext.getApplicationData(),
            ageniumUser,
            savedProsperData,
        );

        await submitDocusign(
            formId,
            instantIulApplicationId,
            calcfocusReportRequest as unknown as CalcfocusReportRequest,
        );

        pollForDocusignComplete(formId, instantIulApplicationId);
    }

    async function checkForDocusignComplete(formId: string, applicationId: string) {
        const requirements = await getApplicationRequirements(formId, applicationId);
        return !!requirements?.find((r) => r.type === 'policyDocusign' && r.status !== 'outstanding');
    }

    async function pollForDocusignComplete(formId: string, applicationId: string) {
        const poll = setInterval(async () => {
            const hasMetRequirements = await checkForDocusignComplete(formId, applicationId);

            if (hasMetRequirements) {
                setIsAwaitingSignature(false);
                clearInterval(poll);
                parameterStore.eAppDocusignCompleted = new Date();
                await contactFormStore.saveForm();
                onDocusignSignComplete();
            }
        }, 5000);
    }

    useEffect(() => {
        ageniumContext.initSdk();
        // Check once initial page load in case signed when agent was not on page
        checkForDocusignComplete(
            parameterStore.formid as string,
            parameterStore.instantIulApplicationId as string,
        ).then(async (completed) => {
            if (completed) {
                parameterStore.eAppDocusignCompleted = new Date();
                await contactFormStore.saveForm();
                onDocusignSignComplete();
            } else setIsCheckForDocusignCompleteDone(true);
        });
    }, []);

    // This is required to load application data, initSdk is not enough.
    useEffect(() => {
        if (!hasLoadedAppData.current && ageniumContext.applicationNumber && !ageniumContext.applicationData) {
            hasLoadedAppData.current = true;
            ageniumContext.loadApplicationData();
        }
    }, [ageniumContext.applicationNumber, ageniumContext.applicationData]);

    return (
        <Grid container className={commonClasses.wrapper}>
            <Grid item>
                <Typography variant="h4" className={commonClasses.title}>
                    Let’s Send Over The Docusign
                </Typography>
                <Grid container spacing={9}>
                    <Grid item>
                        <Typography className={commonClasses.text}>
                            Just click ‘Send Docusign’ at the bottom to provide a Docusign link to the client via email
                            and SMS
                        </Typography>
                    </Grid>
                    {(!isCheckForDocusignCompleteDone || !ageniumContext.applicationData || isAwaitingSignature) && (
                        <CircularProgress />
                    )}
                </Grid>
            </Grid>
            <FixedNavigation
                onBack={onBack}
                isNextDisabled={
                    !isCheckForDocusignCompleteDone ||
                    !ageniumContext.applicationData ||
                    isAwaitingSignature ||
                    isDeathBenefitExceedsMax
                }
                isLoading={false}
                onNext={onSendDocusignClick}
                onNextCustomText="CONFIRM AND SEND DOCUSIGN"
            />
            {isDeathBenefitExceedsMax && (
                <Box style={{margin: '20px auto', width: '60%'}}>
                    <Typography className={classes.warningText}>
                        The face amount is greater than the max approved face amount{' '}
                        {formatCurrency(getMaxFaceAmount())}
                    </Typography>
                </Box>
            )}
            {!IS_PRODUCTION && (
                <div style={{fontSize: '10px', marginTop: 50}}>
                    <div>applicationData</div>
                    <div style={{color: '#880'}}>{JSON.stringify(ageniumContext.applicationData, null, 2)}</div>
                </div>
            )}
        </Grid>
    );
};
